import React, { useCallback, useEffect, useRef, useState } from 'react';
import ResizeHandle from "../AnalysisPanel/hooks/ResizeHandle";
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from '../../../services/api/uiButtonStatsService';

interface RightSidebarProps {
    className?: string;
    children?: React.ReactNode;
    defaultWidth?: number;
    minWidth: number;
    maxWidth: number;
    isMaximized?: boolean;
}

const RightSidebar: React.FC<RightSidebarProps> = ({
    className = '',
    children,
    defaultWidth = 300,
    minWidth,
    maxWidth,
    isMaximized = false,
}) => {
    const [width, setWidth] = useState(defaultWidth);
    const [isResizing, setIsResizing] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const initialX = useRef<number>(0);
    const initialWidth = useRef<number>(0);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        if (isMaximized) return; // Ne pas permettre le redimensionnement si maximisé
        void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.analysisResizeHandle);
        initialX.current = e.clientX;
        initialWidth.current = width;
        setIsResizing(true);
    }, [width, isMaximized]);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizing || isMaximized) return;
        const deltaX = initialX.current - e.clientX;
        const newWidth = Math.max(minWidth, Math.min(maxWidth, initialWidth.current + deltaX));
        setWidth(newWidth);
    }, [isResizing, minWidth, maxWidth, isMaximized]);

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    useEffect(() => {
        if (isMaximized) {
            setWidth(window.innerWidth);
        } else {
            setWidth(defaultWidth);
        }
    }, [isMaximized, defaultWidth]);

    return (
        <div
            ref={panelRef}
            className={`bg-light border-l border-gray-200 flex flex-col p-4 pt-0 h-screen fixed right-0 transition-all duration-200 ease-in-out z-40 ${className}`}
            data-tutorial="flowchart-sidebar"
            style={{
                width: isMaximized ? '100vw' : `${width}px`,
                minWidth: isMaximized ? '100vw' : `${minWidth}px`,
                maxWidth: isMaximized ? '100vw' : `${maxWidth}px`,
                background: '#F9F9F9'
            }}
        >
            {!isMaximized && (
                <ResizeHandle onMouseDown={handleMouseDown} isResizing={isResizing} />
            )}
            {children}
        </div>
    );
};

export { RightSidebar };
